/**
 * Módulo Planos de Ação (MPA) — GUPM-193, fidelidade ao V8 (`_renderMPAList`).
 * 5 pills globais + 5 filtros sobre a carteira achatada em ações; desde o
 * GUPM-332 ela é exibida UM card por plano (ações como subcards) e os planos
 * 100% concluídos vão para a seção colapsável do rodapé. RT/admin cria
 * manualmente ("+ Novo Plano") e avança o status das ações; operador é
 * read-only (RLS é o gate real). O card inteiro abre o detalhe do plano (os botões dentro dele não).
 */
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button, PageHeader } from '@/ui';
import type { AcaoStatus } from '@/domain/planos';
import {
  agruparAcoesPorPlano,
  contarAcoesGlobais,
  filtrarAcoes,
  flattenAcoes,
  gruposQueCasamComContexto,
  particionarGruposPorConclusao,
  type MpaFiltros,
} from '@/domain/planos/mpa';
import { readOpenPlanoId } from '@/domain/planos/navegacao';
import { ChunkErrorBoundary } from '@/components/ChunkErrorBoundary';
import { useHoje, usePlanos, usePlanoMutations } from './hooks';
import { MpaToolbar } from './MpaToolbar';
import { MpaActionList } from './MpaActionList';
import { PlanosConcluidosPanel } from './PlanosConcluidosPanel';
import { PlanoDetail } from './PlanoDetail';

const NovoPlanoModal = lazy(() => import('./NovoPlanoModal'));

const FILTROS_INICIAIS: MpaFiltros = { dam: 'Todas', status: 'Todas', nivel: 'Todos', resp: 'Todos', prazo: 'Todos' };

function errMsg(e: unknown): string | null {
  if (!e) return null;
  return e instanceof Error ? e.message : String(e);
}

export default function PlanosAcao() {
  const { profile } = useAuth();
  const canEdit = profile === 'tecnico_responsavel' || profile === 'admin';
  const { data: planos = [], isLoading, error } = usePlanos();
  const { updateAcao } = usePlanoMutations();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filtros, setFiltros] = useState<MpaFiltros>(FILTROS_INICIAIS);
  const [modalOpen, setModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // GUPM-213: deep-link vindo da baixa da TARP — foca o plano recém-criado. Lê o
  // nav-state efêmero no mount e limpa (replace) para não reabrir em refresh / ao
  // voltar. Mesmo padrão do M3 (openTarpNavState/readOpenTarpId).
  useEffect(() => {
    const openId = readOpenPlanoId(location.state);
    if (openId) {
      setSelectedId(openId);
      navigate('.', { replace: true, state: null });
    }
  }, [location.state, navigate]);

  // `hoje` estável dentro do dia de Brasília e renovado na virada (GUPM-332).
  const hoje = useHoje();
  const acoesFlat = useMemo(() => flattenAcoes(planos), [planos]);
  const contagem = useMemo(() => contarAcoesGlobais(acoesFlat, hoje), [acoesFlat, hoje]);
  const filtradas = useMemo(() => filtrarAcoes(acoesFlat, filtros, hoje), [acoesFlat, filtros, hoje]);

  // GUPM-332: a carteira é exibida agrupada por plano. A lista principal recebe os
  // grupos FILTRADOS menos os planos já 100% concluídos; o painel do rodapé recebe
  // os concluídos derivados da carteira COMPLETA — ele tem filtros próprios, como o
  // `TarpsFechadasPanel` do M3, e não responde à toolbar global.
  const gruposAtivos = useMemo(
    () => particionarGruposPorConclusao(agruparAcoesPorPlano(filtradas, acoesFlat)).ativos,
    [filtradas, acoesFlat],
  );
  const gruposConcluidos = useMemo(
    () => particionarGruposPorConclusao(agruparAcoesPorPlano(acoesFlat, acoesFlat)).concluidos,
    [acoesFlat],
  );
  // Pista do vazio: só os concluídos que o filtro de barragem/nível ativo alcança.
  const concluidosNoFiltro = useMemo(
    () => gruposQueCasamComContexto(gruposConcluidos, filtros).length,
    [gruposConcluidos, filtros],
  );

  const responsaveis = useMemo(
    () => [...new Set(acoesFlat.map((a) => a.responsavel))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [acoesFlat],
  );

  const selected = planos.find((p) => p.id === selectedId) ?? null;

  return (
    <div>
      <PageHeader
        title="Planos de Ação"
        subtitle="Ações corretivas — Barragens A, B, C"
        actions={
          canEdit && !selected ? (
            <Button variant="primary" onClick={() => setModalOpen(true)}>
              + Novo Plano de Ação
            </Button>
          ) : undefined
        }
      />

      {isLoading && <p className="py-6 text-center text-xs text-jf-muted">Carregando…</p>}
      {error && (
        <p className="py-6 text-center text-xs text-level-emergencia">Falha ao carregar planos: {errMsg(error)}</p>
      )}

      {!isLoading && !error && (
        <>
          {selected ? (
            <PlanoDetail
              plano={selected}
              canEdit={canEdit}
              updating={updateAcao.isPending}
              onBack={() => setSelectedId(null)}
              onUpdateAcao={(acaoId, status) => updateAcao.mutate({ planoId: selected.id, acaoId, status })}
            />
          ) : (
            <>
              <MpaToolbar contagem={contagem} filtros={filtros} responsaveis={responsaveis} onChange={setFiltros} />
              {updateAcao.isError && (
                <p className="mb-2 text-xs text-level-emergencia">Falha ao atualizar ação: {errMsg(updateAcao.error)}</p>
              )}
              <MpaActionList
                grupos={gruposAtivos}
                hoje={hoje}
                canEdit={canEdit}
                updating={updateAcao.isPending}
                planosConcluidos={concluidosNoFiltro}
                onOpenPlano={setSelectedId}
                onUpdateAcao={(planoId, acaoId, status: AcaoStatus) => updateAcao.mutate({ planoId, acaoId, status })}
              />
              <PlanosConcluidosPanel
                grupos={gruposConcluidos}
                hoje={hoje}
                abrirPorPedidoExplicito={filtros.status === 'concluido'}
                onOpenPlano={setSelectedId}
              />
            </>
          )}
        </>
      )}

      {modalOpen && (
        // GUPM-302: sem o boundary, chunk de deploy anterior estourava dentro do
        // `Suspense` com `fallback={null}` e desmontava a árvore inteira — tela branca.
        // `aoFechar` é obrigatório aqui: o boundary está no lugar do MODAL, então precisa
        // aparecer como overlay e devolver o `modalOpen` para `false`. Sem isso o
        // `onClose` do `NovoPlanoModal` nunca monta, o estado fica preso em `true` e o
        // botão "+ Novo Plano de Ação" vira no-op.
        <ChunkErrorBoundary aoFechar={() => setModalOpen(false)}>
          <Suspense fallback={null}>
            <NovoPlanoModal onClose={() => setModalOpen(false)} />
          </Suspense>
        </ChunkErrorBoundary>
      )}
    </div>
  );
}
